import { AlertTriangle, CheckCircle2, Flag, Gift, Plus, ShieldOff, XCircle } from "lucide-solid";
import { Show, createEffect, createMemo, createSignal } from "solid-js";
import { DUEL_STATUS_VARIANT, duelScoreOutcome, owesNothingToRefuse, sealType } from "system";
import type { DuelResultSubmission, DuelStatus, RecordingMode, Tier, User } from "system";
import { DriverCard, type PinnedCategory } from "~/components/driver-card";
import { Emboss } from "~/components/emboss";
import { PageContainer } from "~/components/layout/page";
import { MatchTimeline } from "~/components/match-timeline";
import { nextRoundFor } from "~/lib/duel-matches";
import { RulesDialog } from "~/components/rules-dialog";
import { Avatar } from "~/components/ui/avatar";
import { Badge, type BadgeVariant } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardHeader, CardTitle } from "~/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "~/components/ui/dialog";
import { Input, Textarea } from "~/components/ui/input";
import { ToggleChip } from "~/components/ui/toggle-chip";
import { duelsApi, playersApi, reportsApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import { requireFeature } from "~/lib/features";
import { QueryKey } from "~/lib/query-keys";
import { CAR_CLASS_LABELS, RACE_TYPE_LABELS, formatDateTime, timeUntil } from "~/lib/utils";

import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { createFileRoute } from "@tanstack/solid-router";
import { Link } from "@tanstack/solid-router";

export const Route = createFileRoute("/_authed/duels/$duelId")({
  beforeLoad: () => requireFeature("duels"),
  component: DuelDetailPage,
});

function DuelDetailPage() {
  const params = Route.useParams();
  const { state } = useAuth();
  const qc = useQueryClient();

  const duel = createQuery(() => ({
    queryKey: [QueryKey.Duel, params().duelId],
    queryFn: () => duelsApi.get(params().duelId),
    enabled: !!params().duelId,
  }));

  const myProfile = createQuery(() => ({
    queryKey: [QueryKey.Profile, state.user?.id],
    queryFn: () => playersApi.get(state.user!.id),
    enabled: !!state.user?.id,
    staleTime: 60_000,
  }));

  const attacker = createQuery(() => ({
    queryKey: [QueryKey.Profile, duel.data?.attackerId],
    queryFn: () => playersApi.get(duel.data?.attackerId!),
    enabled: !!duel.data?.attackerId
  }));

  const defender = createQuery(() => ({
    queryKey: [QueryKey.Profile, duel.data?.defenderId],
    queryFn: () => playersApi.get(duel.data?.defenderId!),
    enabled: !!duel.data?.defenderId
  }));

  const isParticipant = () =>
    duel.data?.attackerId === state.user?.id || duel.data?.defenderId === state.user?.id;
  const isDefender = () => duel.data?.defenderId === state.user?.id;
  // Spectators always see the score. Participants see action buttons while the duel
  // is live, then the final score once it completes (there is no longer a match
  // timeline for score-mode duels to fall back on).
  const showScore = () => !isParticipant() || duel.data?.status === "completed";
  const availableVouchers = () => myProfile.data?.availableVouchers ?? 0;
  const pinnedCategory = (key: "attacker" | "defender"): PinnedCategory | undefined =>
    duel.data ? { raceType: duel.data.raceType, carClass: duel.data.carClass, tier: duel.data[`${key}Tier`] as Tier | undefined } : undefined;

  const accept = createMutation(() => ({
    mutationFn: () => duelsApi.accept(params().duelId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QueryKey.Duel, params().duelId] });
      qc.invalidateQueries({ queryKey: [QueryKey.Rankings, "rivals"] });
      if (state.user?.id) qc.invalidateQueries({ queryKey: [QueryKey.Profile, state.user.id] });
    },
  }));

  const decline = createMutation(() => ({
    mutationFn: () => duelsApi.decline(params().duelId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QueryKey.Duel, params().duelId] });
      qc.invalidateQueries({ queryKey: [QueryKey.Rankings, "rivals"] });
      if (state.user?.id) qc.invalidateQueries({ queryKey: [QueryKey.Profile, state.user.id] });
    },
  }));

  return (
    <PageContainer>
      <Show when={duel.data} keyed fallback={<div class="h-64 bg-surface-2 rounded-xl animate-pulse" />}>
        {(d) => (
          <div class="space-y-6">
            {/* ── Dispute panel ─────────────────────────────────────────────── */}
            <Show when={d.status === "disputed"}>
              <DisputePanel
                duelId={d.id}
                attacker={d.attacker!}
                defender={d.defender!}
                recordingMode={d.recordingMode}
                submissions={d.resultSubmissions ?? []}
                isJudge={state.user?.role !== "member"}
                viewerIsParticipant={isParticipant()}
              />
            </Show>

            {/* ── Hero card ─────────────────────────────────────────────────── */}
            {/* isolate: one stacking context for the whole clash, so the three depth layers
                resolve here and nowhere else — seals at the back, the cards (and their drop +
                glow shadows) above them, the scores on top so no shadow washes over them. */}
            <div class="relative isolate">
              {/* Header — status, category and mode reflow as one group. The actions
                  cluster sits alone top-right on mobile, far-right on the single sm+ row. */}
              <div class="mb-4">
                <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                  {/* Actions — alone on the first line (narrow), pushed right (wider) */}
                  <div class="order-1 min-[480px]:order-2 ml-auto flex items-center gap-2">
                    {/* Reportable in any state, including after completion: cheating
                        (exploit tunes, OP cars) often surfaces on a later replay review.
                        A completed duel stays immutable — the report resolves via the
                        ranking tools, never by reopening (see report detail). */}
                    <Show when={isParticipant()}>
                      <ReportDialog duelId={params().duelId} />
                    </Show>
                    <RulesDialog scope="duels" />
                  </div>
                  {/* Meta — spreads full-width on its own line (narrow), tight-left (wider) */}
                  <div class="order-2 min-[480px]:order-1 flex items-center justify-between gap-3 w-full min-[480px]:w-auto">
                    <StatusBadge status={d.status} winnerId={d.winnerId} />
                    <span class="text-xs font-semibold text-text-faint uppercase tracking-wide">
                      {RACE_TYPE_LABELS[d.raceType]} · {CAR_CLASS_LABELS[d.carClass]}
                    </span>
                    <Badge variant="default">{d.recordingMode === "full" ? "Per-match" : "Score only"}</Badge>
                  </div>
                </div>
                <Show when={d.status === "completed" && !d.winnerId}>
                  <p class="text-xs text-text-faint mt-2">
                    Defender keeps their tier, no ranking change.
                  </p>
                </Show>
              </div>

              {/* Attacker */}
              <Show when={attacker.data}>
                <Link to="/profile/$username" params={{ username: attacker.data!.username }} search={{ tab: "duels" }} class="relative z-30 block">
                  <DriverCard profile={attacker.data!} tagline="Attacker" pinnedCategory={pinnedCategory("attacker")} />
                </Link>
              </Show>

              {/* VS strip — buttons hug the edges, VS owns the middle gap and scales down on mobile */}
              <div class="flex items-center justify-between sm:justify-center gap-2 sm:gap-8 my-3 relative px-1 sm:px-6 py-2 sm:py-8">
                {/* The duel's type seal (同輩/格上/豪傑 by tier gap) pressed into the strip,
                    naming wordlessly what kind of duel this is. On desktop it sits in the open
                    side gutters, one seal per flank, framing the clash without crowding the
                    scores; on mobile (where the scores hug the edges) one copy sits centred
                    behind the VS. They ride -z-10, so they read behind the scores even where a
                    flank extends toward the middle. overflow-hidden keeps the relief from
                    bleeding onto the cards above and below. Sizes are eyeball-tuned. */}
                <Show when={sealType(d.attackerTier, d.defenderTier)}>
                  {(mark) => {
                    // A forced duel is binding; light its seals in brand red so the strip reads
                    // "hot" at a glance. The drop-shadows take no colour, so they glow in the
                    // text-brand currentColor; full opacity lifts the relief out of the faint
                    // resting state. (Forced is server-authoritative, mirrored to the client.)
                    const lit = d.forced
                      ? "opacity-100 text-brand [filter:drop-shadow(0_0_3px)_drop-shadow(0_0_9px)]"
                      : "opacity-60";
                    return (
                      <>
                        <Emboss
                          mark={mark()}
                          class={`sm:hidden absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 h-12 ${lit}`}
                        />
                        <Emboss
                          mark={mark()}
                          class={`hidden sm:block absolute left-4 top-1/2 z-10 -translate-y-1/2 h-20 ${lit}`}
                        />
                        <Emboss
                          mark="seal"
                          class={`hidden sm:block absolute right-4 top-1/2 z-10 -translate-y-1/2 h-20 ${lit}`}
                        />
                      </>
                    );
                  }}
                </Show>
                {/* Left — attacker score (spectator) or accept/forfeit (participant) */}
                <div class="relative z-20 flex items-center gap-2 min-w-0">
                  <Show when={showScore()}>
                    <span class="font-display text-4xl sm:text-5xl font-black tabular-nums leading-none text-outline-lg">{d.attackerScore}</span>
                    <span class="text-label-xs text-text-faint uppercase tracking-wide">Attacker</span>
                  </Show>
                  <Show when={d.status === "pending" && isDefender()}>
                    <Button size="sm" onClick={() => accept.mutate()} disabled={accept.isPending}>
                      <CheckCircle2 size={14} /> Accept
                    </Button>
                  </Show>
                  <Show when={d.status === "active" && isParticipant()}>
                    <ForfeitDialog duelId={params().duelId} />
                  </Show>
                </div>

                <span class="relative z-20 font-display text-3xl sm:text-6xl font-black tracking-widest shrink-0 text-outline-md sm:text-outline-lg select-none">VS</span>

                {/* Right — defender score (spectator) or defer/submit (participant) */}
                <div class="relative z-20 flex items-center gap-2 min-w-0">
                  <Show when={showScore()}>
                    <span class="text-label-xs text-text-faint uppercase tracking-wide">Defender</span>
                    <span class="font-display text-4xl sm:text-5xl font-black tabular-nums leading-none text-outline-lg">{d.defenderScore}</span>
                  </Show>
                  <Show when={d.status === "pending" && isDefender()}>
                    {(() => {
                      // An un-forced giant-kill (豪傑) is refusable for free, so the voucher gate
                      // must not disable it; it is a flat decline, not a voucher-funded defer.
                      // Mirrors owesNothingToRefuse on the server, the single source of truth.
                      const free =
                        !!d.attackerTier &&
                        !!d.defenderTier &&
                        owesNothingToRefuse(sealType(d.attackerTier, d.defenderTier), d.forced);
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => decline.mutate()}
                          disabled={decline.isPending || (!free && availableVouchers() === 0)}
                          title={
                            free
                              ? "Refuse this challenge for free, no voucher spent"
                              : availableVouchers() === 0
                                ? "No vacation vouchers available"
                                : `Defer using a voucher (${availableVouchers()} left)`
                          }
                        >
                          <XCircle size={14} /> {free ? "Decline" : "Defer"}
                        </Button>
                      );
                    })()}
                  </Show>
                  <Show when={d.status === "active" && isParticipant()}>
                    <Show when={d.resultSubmissions?.find(s => s.submitterId === state.user?.id)} keyed>
                      {(sub) => (
                        <span class="text-label-xs text-amber-400">
                          Submitted {sub.attackerScore}–{sub.defenderScore}
                        </span>
                      )}
                    </Show>
                    <Show
                      when={d.recordingMode === "full"}
                      fallback={<SubmitResultDialog duelId={params().duelId} mySubmission={d.resultSubmissions?.find(s => s.submitterId === state.user?.id)} />}
                    >
                      <SubmitMatchDialog duelId={params().duelId} attacker={d.attacker!} defender={d.defender!} nextRound={nextRoundFor(d.matches, state.user?.id)} />
                    </Show>
                  </Show>
                </div>
              </div>

              {/* Defender */}
              <Show when={defender.data}>
                <Link to="/profile/$username" params={{ username: defender.data!.username }} search={{ tab: "duels" }} class="relative z-30 block">
                  <DriverCard profile={defender.data!} tagline="Defender" pinnedCategory={pinnedCategory("defender")} />
                </Link>
              </Show>

              {/* Deadlines footer */}
              <div class="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 text-xs text-text-faint">
                <span>Created: {formatDateTime(d.createdAt)}</span>
                <Show when={d.status === "pending"}>
                  <span class="text-yellow-400">Respond by: {timeUntil(d.respondDeadline)}</span>
                </Show>
                <Show when={d.status === "pending" && isParticipant() && !isDefender() && defender.data}>
                  <a
                    href={`https://discord.com/users/${defender.data!.discordId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="hover:text-text-base transition-colors"
                  >
                    DM {defender.data!.displayName} on Discord →
                  </a>
                </Show>
                <Show when={d.status !== "completed"}>
                  <span>Duel deadline: {timeUntil(d.duelDeadline)}</span>
                </Show>
                <Show when={d.status === "active" || d.status === "pending"}>
                  <span class="flex items-center gap-1">
                    <Gift size={11} /> Winner earns a vacation voucher
                  </span>
                </Show>
              </div>
            </div>

            {/* ── Match timeline — full mode only; score duels have no rounds ── */}
            <Show when={d.recordingMode === "full"}>
              <Card>
                <CardHeader>
                  <CardTitle>Match History</CardTitle>
                </CardHeader>
                <Show when={d.attacker && d.defender}>
                  <MatchTimeline
                    matches={d.matches ?? []}
                    status={d.status}
                    attacker={d.attacker!}
                    defender={d.defender!}
                    attackerScore={d.attackerScore}
                    defenderScore={d.defenderScore}
                  />
                </Show>
              </Card>
            </Show>
          </div>
        )}
      </Show>
    </PageContainer>
  );
}

// A completed duel with no winner is a draw (an equal-score draw or a double
// forfeit). Derives the status chip label and variant from that.
function duelStatusView(status: DuelStatus, winnerId: string | null) {
  const isDraw = status === "completed" && !winnerId;
  const variant: BadgeVariant = isDraw ? "warning" : DUEL_STATUS_VARIANT[status];
  return { variant, label: isDraw ? "Draw" : status };
}

// The duel status as a chip for the header meta group. The draw outcome note is
// rendered separately by the header, as it needs its own full-width line.
function StatusBadge(props: { status: DuelStatus; winnerId: string | null }) {
  const view = createMemo(() => duelStatusView(props.status, props.winnerId));
  return (
    <Badge variant={view().variant} class="uppercase">
      {view().label}
    </Badge>
  );
}

function SubmitMatchDialog(props: { duelId: string; attacker: User; defender: User; nextRound: number }) {
  const [open, setOpen] = createSignal(false);
  const [raceName, setRaceName] = createSignal("");
  const [winnerId, setWinnerId] = createSignal("");
  const qc = useQueryClient();

  const submit = createMutation(() => ({
    mutationFn: () =>
      duelsApi.submitMatch(props.duelId, { roundNumber: props.nextRound, raceName: raceName(), winnerId: winnerId() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QueryKey.Duel, props.duelId] });
      setOpen(false);
      setRaceName(""); setWinnerId("");
    },
  }));

  return (
    <Dialog open={open()} onOpenChange={setOpen}>
      <DialogTrigger as={Button} variant="default" size="sm">
        <Plus size={14} /> Submit Match
      </DialogTrigger>
      <DialogContent title="Submit Match Result">
        <div class="space-y-3">
          <div class="space-y-1.5">
            <label class="text-xs font-semibold text-text-muted uppercase tracking-wide">Race Name</label>
            <Input placeholder="e.g. Horizon Festival Circuit 1" value={raceName()} onInput={(e) => setRaceName(e.currentTarget.value)} />
          </div>
          <div class="space-y-1.5">
            <label class="text-xs font-semibold text-text-muted uppercase tracking-wide">Winner</label>
            <div class="flex gap-2">
              {[props.attacker, props.defender].map((p) => (
                <ToggleChip
                  active={winnerId() === p.id}
                  onClick={() => setWinnerId(p.id)}
                  class="flex-1 p-2.5 justify-start"
                >
                  <Avatar src={p.avatarUrl ?? undefined} fallback={p.displayName} size="sm" />
                  <span class="text-sm font-semibold">{p.displayName}</span>
                </ToggleChip>
              ))}
            </div>
          </div>
          <Show when={submit.error}>
            <p class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{submit.error?.message}</p>
          </Show>
          <div class="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => submit.mutate()} disabled={!raceName() || !winnerId() || submit.isPending}>
              {submit.isPending ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubmitResultDialog(props: { duelId: string; mySubmission: DuelResultSubmission | undefined }) {
  const [open, setOpen] = createSignal(false);
  const [attackerScore, setAttackerScore] = createSignal(props.mySubmission?.attackerScore ?? 0);
  const [defenderScore, setDefenderScore] = createSignal(props.mySubmission?.defenderScore ?? 0);
  const qc = useQueryClient();

  const submit = createMutation(() => ({
    mutationFn: () =>
      duelsApi.submitResult(props.duelId, { attackerScore: attackerScore(), defenderScore: defenderScore() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QueryKey.Duel, props.duelId] });
      setOpen(false);
    },
  }));

  const label = () => props.mySubmission ? "Update Score" : "Submit Score";

  // A final score must be one the duel could legally end on (a +WIN_LEAD win or a valid
  // draw); the same rule the server enforces. Pre-checking here blocks the submit instead
  // of firing a request that would only come back 422. Stay quiet on an untouched 0–0 -
  // the hint is guidance after they've started entering, not a scold on an empty form.
  const outcome = createMemo(() => duelScoreOutcome(attackerScore(), defenderScore()));
  const incompleteHint = () => {
    const o = outcome();
    if (o.kind !== "incomplete") return null;
    return attackerScore() > 0 || defenderScore() > 0 ? o.reason : null;
  };

  return (
    <Dialog open={open()} onOpenChange={setOpen}>
      <DialogTrigger as={Button} variant="default" size="sm">
        <Plus size={14} /> {label()}
      </DialogTrigger>
      <DialogContent title="Submit Final Score">
        <div class="space-y-3">
          <Show when={props.mySubmission}>
            <p class="text-xs text-text-faint bg-surface-2 rounded-lg px-3 py-2">
              Your previous submission: {props.mySubmission!.attackerScore} – {props.mySubmission!.defenderScore}. Submitting again will update it.
            </p>
          </Show>
          <div class="flex gap-4">
            <div class="flex-1 space-y-1.5">
              <label class="text-xs font-semibold text-text-muted uppercase tracking-wide">Attacker score</label>
              <Input type="number" min={0} value={attackerScore()} onInput={(e) => setAttackerScore(Number(e.currentTarget.value))} />
            </div>
            <div class="flex-1 space-y-1.5">
              <label class="text-xs font-semibold text-text-muted uppercase tracking-wide">Defender score</label>
              <Input type="number" min={0} value={defenderScore()} onInput={(e) => setDefenderScore(Number(e.currentTarget.value))} />
            </div>
          </div>
          <Show when={incompleteHint()}>
            {(reason) => (
              <p class="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">{reason()}</p>
            )}
          </Show>
          <Show when={submit.error}>
            <p class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{submit.error?.message}</p>
          </Show>
          <div class="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => submit.mutate()} disabled={submit.isPending || outcome().kind === "incomplete"}>
              {submit.isPending ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DisputePanel(props: {
  duelId: string;
  attacker: User;
  defender: User;
  recordingMode: RecordingMode;
  submissions: DuelResultSubmission[];
  isJudge: boolean;
  viewerIsParticipant: boolean;
}) {
  const qc = useQueryClient();

  const resolve = createMutation(() => ({
    mutationFn: (verdict: "attacker" | "defender" | "replay") =>
      duelsApi.resolveDispute(props.duelId, verdict),
    onSuccess: () => {
      // The ruling changes the winner's ranking, so refresh tiers/cards and the
      // duels list for the resolver, not only the duel detail. (Other clients,
      // e.g. the winner's own dashboard, update via realtime.)
      qc.invalidateQueries({ queryKey: [QueryKey.Duel, props.duelId] });
      qc.invalidateQueries({ queryKey: [QueryKey.Duels] });
      qc.invalidateQueries({ queryKey: [QueryKey.Profile] });
      qc.invalidateQueries({ queryKey: [QueryKey.Rankings] });
    },
  }));

  const attackerSub = () => props.submissions.find(s => s.submitterId === props.attacker.id);
  const defenderSub = () => props.submissions.find(s => s.submitterId === props.defender.id);

  return (
    <Card class="border-red-500/30 bg-red-500/5">
      <CardHeader>
        <CardTitle class="text-red-400 flex items-center gap-2">
          <AlertTriangle size={15} /> Disputed Result
        </CardTitle>
      </CardHeader>

      <Show when={props.recordingMode === "summary" && (attackerSub() || defenderSub())}>
        <div class="grid grid-cols-2 gap-3 mb-4">
          <Show when={attackerSub()} keyed>
            {(sub) => (
              <div class="bg-surface-3 rounded-lg p-3 text-center">
                <p class="text-xs text-text-faint mb-1">{props.attacker.displayName} submitted</p>
                <p class="font-display text-2xl font-bold text-text-base whitespace-nowrap">{sub.attackerScore} - {sub.defenderScore}</p>
              </div>
            )}
          </Show>
          <Show when={defenderSub()} keyed>
            {(sub) => (
              <div class="bg-surface-3 rounded-lg p-3 text-center">
                <p class="text-xs text-text-faint mb-1">{props.defender.displayName} submitted</p>
                <p class="font-display text-2xl font-bold text-text-base whitespace-nowrap">{sub.attackerScore} - {sub.defenderScore}</p>
              </div>
            )}
          </Show>
        </div>
      </Show>

      <Show
        when={props.isJudge && !props.viewerIsParticipant}
        fallback={
          <Show
            when={props.viewerIsParticipant}
            fallback={
              <p class="text-sm text-text-muted">
                A judge has been notified and will review this dispute.
              </p>
            }
          >
            <div class="rounded-lg border border-border bg-surface-3 px-3 py-2 text-sm text-text-muted flex items-start gap-2">
              <ShieldOff size={14} class="shrink-0 mt-0.5 text-text-faint" />
              <span>
                You're a participant in this duel, so the verdict isn't yours to make — another judge will rule on it. This recusal is automatic to keep the result unbiased.
              </span>
            </div>
          </Show>
        }
      >
        <div class="space-y-2">
          <p class="text-xs text-text-faint uppercase tracking-wide font-semibold">Judge verdict</p>
          <Show when={resolve.error}>
            <p class="text-sm text-red-400">{resolve.error?.message}</p>
          </Show>
          <div class="flex flex-col sm:flex-row gap-2 flex-wrap">
            <Button size="sm" class="flex-1 hover:flex-2 whitespace-nowrap py-2" onClick={() => resolve.mutate("attacker")} disabled={resolve.isPending}>
              {props.attacker.displayName} wins
            </Button>
            <Button size="sm" variant="outline" class="flex-1 hover:flex-2 whitespace-nowrap py-2" onClick={() => resolve.mutate("replay")} disabled={resolve.isPending}>
              Replay
            </Button>
            <Button size="sm" class="flex-1 hover:flex-2 whitespace-nowrap py-2" onClick={() => resolve.mutate("defender")} disabled={resolve.isPending}>
              {props.defender.displayName} wins
            </Button>
          </div>
        </div>
      </Show>
    </Card>
  );
}

function ForfeitDialog(props: { duelId: string }) {
  const [open, setOpen] = createSignal(false);
  const qc = useQueryClient();
  const { state } = useAuth();

  const forfeit = createMutation(() => ({
    mutationFn: () => duelsApi.forfeit(props.duelId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QueryKey.Duel, props.duelId] });
      qc.invalidateQueries({ queryKey: [QueryKey.Rankings, "rivals"] });
      if (state.user?.id) qc.invalidateQueries({ queryKey: [QueryKey.Profile, state.user.id] });
      setOpen(false);
    },
  }));

  return (
    <Dialog open={open()} onOpenChange={setOpen}>
      <DialogTrigger as={Button} variant="danger-outline" size="sm">
        <ShieldOff size={13} /> Forfeit
      </DialogTrigger>
      <DialogContent title="Forfeit Duel">
        <div class="space-y-3">
          <p class="text-sm text-text-muted">
            If both drivers forfeit, the duel ends as a tie and rankings are unchanged.
            If only you forfeit, your opponent wins automatically.
          </p>
          <Show when={forfeit.error}>
            <p class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{forfeit.error?.message}</p>
          </Show>
          <div class="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => forfeit.mutate()} disabled={forfeit.isPending}>
              {forfeit.isPending ? "Forfeiting..." : "Confirm Forfeit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog(props: { duelId: string }) {
  const draftKey = () => `draft:report:${props.duelId}`;
  const draft = (() => { try { const r = sessionStorage.getItem(draftKey()); return r ? JSON.parse(r) : null; } catch { return null; } })();

  const [open, setOpen] = createSignal(false);
  const [issue, setIssue] = createSignal(draft?.issue ?? "");
  const [evidence, setEvidence] = createSignal(draft?.evidence ?? "");

  createEffect(() => {
    try { sessionStorage.setItem(draftKey(), JSON.stringify({ issue: issue(), evidence: evidence() })); } catch {}
  });

  const report = createMutation(() => ({
    mutationFn: () =>
      reportsApi.create({ duelId: props.duelId, issue: issue(), evidenceLink: evidence() || undefined }),
    onSuccess: () => {
      try { sessionStorage.removeItem(draftKey()); } catch {}
      setOpen(false); setIssue(""); setEvidence("");
    },
  }));

  return (
    <Dialog open={open()} onOpenChange={setOpen}>
      <DialogTrigger as={Button} variant="ghost" size="sm">
        <Flag size={13} /> Report
      </DialogTrigger>
      <DialogContent title="Submit a Report">
        <div class="space-y-3">
          <div class="space-y-1.5">
            <label class="text-xs font-semibold text-text-muted uppercase tracking-wide">Issue Description</label>
            <Textarea placeholder="Describe the rule violation or issue..." value={issue()} onInput={(e) => setIssue(e.currentTarget.value)} />
          </div>
          <div class="space-y-1.5">
            <label class="text-xs font-semibold text-text-muted uppercase tracking-wide">Evidence Link</label>
            <Input placeholder="Video proof URL..." value={evidence()} onInput={(e) => setEvidence(e.currentTarget.value)} />
          </div>
          <div class="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => report.mutate()} disabled={issue().length < 10 || report.isPending}>
              {report.isPending ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
